import { LAST_HOTEL_SEARCH, routePaths } from 'utils/constants';
import UserProfileMenu from 'components/admin/navigation/userProfileMenu';
import { createSearchParams, useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { orgSelector } from 'src/redux/selectors';
import ProgramDropDown from 'components/program/ProgramDropDown';
import MyTripsMenu from 'components/myTrips/MyTripsMenu';
import { getPath } from 'utils/utils';
import './OrganizationHeader.scss';
import { searchFilterLocalStorage } from 'src/types/hotel.type';
import { useSiteConfig } from 'src/contexts/SiteGuardContext';
import { formatOrgName } from 'src/utils/organization.util';
import AppImage from 'components/image/AppImage';
import { isEmpty } from 'lodash';

type OrganizationHeaderProps = {
   showProgram?: boolean;
   showMyTrips?: boolean;
   hideBackground?: boolean;
};

const OrganizationHeader = (props: OrganizationHeaderProps) => {
   const navigate = useNavigate();
   const params = useParams();
   const { organization } = useSelector(orgSelector);
   const { siteConfig } = useSiteConfig();
   const primaryLogo = siteConfig?.visualBranding?.primaryLogo;
   const { showProgram, showMyTrips } = props;

   const handleLogoClick = () => {
      // If user is on organization-specific pages, navigate to org landing page
      const lastHotelSearch: string | null = localStorage.getItem(LAST_HOTEL_SEARCH);
      let _search = '';

      if (lastHotelSearch) {
         try {
            const searchObject: searchFilterLocalStorage = JSON.parse(lastHotelSearch);
            const stringifiedObject = Object.fromEntries(Object.entries(searchObject).map(([key, value]) => [key, String(value)]));
            _search = createSearchParams(stringifiedObject).toString();
         } catch (e) {
            console.error('Failed to parse last hotel search', e);
         }
      }

      if (organization?.organizationType === 'SINGLE_EVENT' && !isEmpty(organization.events)) {
         navigate(getPath(routePaths.EVENT.LANDING, { organizationSlug: organization?.slug, eventSlug: organization.events[0].slug }));
      } else if (params.organizationSlug && organization?.slug) {
         navigate({
            pathname: getPath(routePaths.ORGANIZATION.DETAILS, { organizationSlug: organization.slug }),
         });
      } else {
         navigate(routePaths.LANDING);
      }
   };

   // relative z-30: the mobile account panel is a flowbite Drawer, which renders in place
   // rather than through a portal, so it cannot escape this header's subtree. Page content
   // reaches the root stacking context at z-20 (the org landing hero) and would otherwise
   // paint over the open drawer. 30 clears that while staying under the portaled overlays
   // (SlideUpModal's z-40 backdrop / z-50 sheet) so those still dim the header.
   return (
      <header className={`organization-header relative z-30 flex justify-between items-center w-full p-3.75 md:px-7.5 md:py-3.75 bg-white`}>
         <div className="flex flex-wrap xl:flex-nowrap items-center gap-2 lg:gap-4">
            <div className={`flex justify-start items-center gap-1.25 flex-wrap md:flex-nowrap xl:gap-7.5 min-h-[43px] md:h-10`}>
               <button onClick={handleLogoClick}>
                  <AppImage
                     src={primaryLogo}
                     alt={`${siteConfig?.displayName || 'Site'} Logo`}
                     className="h-6.5 w-[107px] xs:w-auto xs:h-9 sm:h-11 md:h-8.5 xl:h-11 object-contain"
                  />
               </button>
               {organization?.name && (
                  <div className="w-full">
                     <div
                        className={`text-xs leading-4 md:text-l-base md:leading-5 xl:text-l-lg xl:leading-6 font-bold text-black truncate w-[115px] sm:w-auto`}
                     >
                        {formatOrgName(organization, { form: 'full' })}
                     </div>
                  </div>
               )}
               {showProgram && (
                  <div className="program-wrapper w-full md:w-auto">
                     <ProgramDropDown dropdownType="program" />
                  </div>
               )}
            </div>
         </div>
         <div className="flex justify-end gap-2.5">
            <div className="flex items-center justify-end sm:gap-2">
               {showMyTrips && <MyTripsMenu />}
               <div className="flex items-center -mr-2 sm:mr-0 z-20">
                  <UserProfileMenu organization={{ color: '#801845' }} />
               </div>
            </div>
         </div>
      </header>
   );
};

export default OrganizationHeader;
